import { ExtendedTemplateComponent, ExtendedTemplateComponentMetadata } from "./typings";
import { ModuleDefinition, FlexComponent } from "src/typings/flexFramework/FlexComponent";
import { DescendantDepth, getNextDepth } from "src/stores/ExperienceTemplateStore";

const isFlexComponent = (flexEntity: ModuleDefinition | FlexComponent): flexEntity is FlexComponent => {
  return (flexEntity as FlexComponent).model !== undefined;
};

const getNameFromFlexEntity = (flexEntity: ModuleDefinition | FlexComponent): string => {
  let name = "";

  if (isFlexComponent(flexEntity)) {
    if (flexEntity.type === "Region") {
      name = "region";
    } else if (flexEntity.type === "Module") {
      name = flexEntity.name;
    } else {
      // probably a TAB
      name = "tab";
    }
  } else {
    if (flexEntity.fmType === "REGION") {
      name = "region";
    } else if (flexEntity.fmType === "TAB") {
      name = "tab";
    } else {
      name = flexEntity.name;
    }
  }

  return name;
};

/**
 * Converts a value that should be a boolean, but is maybe a string, to a boolean. If the value is undefined, the function will return undefined
 * This is necessary to deal with complex behavior of compositor and it's inconsistent treatment of some data types
 */
const stringToBoolean = (value?: string | boolean): boolean | undefined => {
  if (value === undefined) {
    return undefined;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    return value.toLowerCase() === "true";
  }

  return !!value;
};

/**
 * creates ExtendedTemplateComponent equivalent of provided ModuleDefinition or FlexComponent
 * @param {ModuleDefinition | FlexComponent} flexEntity
 * @param {DescendantDepth} descendantDepth specify depth of returned TemplateComponent tree
 * @returns ExtendedTemplateComponent
 */

export const flexToExtendedTemplateComponent = (
  flexEntity: FlexComponent | ModuleDefinition,
  descendantDepth: DescendantDepth
): ExtendedTemplateComponent => {
  const type = getNameFromFlexEntity(flexEntity);

  const view =
    flexEntity.view !== undefined ? flexEntity.view : isFlexComponent(flexEntity) ? flexEntity.model?.view : undefined;

  const ajaxOnly =
    flexEntity.ajaxOnly !== undefined
      ? stringToBoolean(flexEntity.ajaxOnly)
      : isFlexComponent(flexEntity)
      ? stringToBoolean(flexEntity.model?.ajaxOnly)
      : undefined;

  const clientSideOnly =
    flexEntity.clientSideOnly !== undefined
      ? stringToBoolean(flexEntity.clientSideOnly)
      : isFlexComponent(flexEntity)
      ? stringToBoolean(flexEntity.model?.clientSideOnly)
      : undefined;

  const configFromFlexEntity = { ...flexEntity } as any;

  // delete fields from config that are already represented elsewhere in the TemplateComponent
  delete configFromFlexEntity.id;
  delete configFromFlexEntity.fmId;
  delete configFromFlexEntity.name;
  delete configFromFlexEntity.view;
  delete configFromFlexEntity.fmType;
  delete configFromFlexEntity.type;
  delete configFromFlexEntity.model;
  delete configFromFlexEntity.modules;
  delete configFromFlexEntity.children;
  delete configFromFlexEntity.groups;

  // if composition based rendering and component is clientSideOnly or equivalent, it's model contains properties from module definition that should be carried over to config
  let configFromModel = {} as any;

  if (
    isFlexComponent(flexEntity) &&
    (clientSideOnly || ajaxOnly || type === "tab" || type === "region" || type === "tab-group")
  ) {
    configFromModel = { ...flexEntity.model };

    // delete fields from config that are already represented elsewhere in the TemplateComponent
    delete configFromModel.id;
    delete configFromModel.name;
    delete configFromModel.view;
    delete configFromModel.fmType;
    delete configFromModel.ajaxOnly;
    delete configFromModel.clientSideOnly;
  }

  const config = {
    ...configFromFlexEntity,
    ...configFromModel,
    ajaxOnly,
    clientSideOnly,
    view,
  } as any;

  const metadata: ExtendedTemplateComponentMetadata = {
    id: `${flexEntity.id}`,
  };

  if (type === "tab" || type === "region") {
    metadata.name = flexEntity.name;
  }

  const templateComponent: ExtendedTemplateComponent = {
    type,
    metadata,
    config,
  };

  if ((flexEntity.modules || flexEntity.groups || flexEntity.children) && descendantDepth !== DescendantDepth.NONE) {
    const childFlexComponents: (ModuleDefinition | FlexComponent)[] = [
      ...(flexEntity.modules || []),
      ...(flexEntity.groups || []),
      ...(flexEntity.children || []),
    ];

    const childTemplateComponents = childFlexComponents
      .map((child) => flexToExtendedTemplateComponent(child, getNextDepth(descendantDepth)))
      .filter((tc: ExtendedTemplateComponent) => !!tc);

    if (childTemplateComponents.length > 0) {
      templateComponent.children = childTemplateComponents;
    }
  }

  return templateComponent;
};
